export enum WarehouseErrorCode {
  INSUFFICIENT_STOCK = 'INSUFFICIENT_STOCK',
  ZONE_NOT_FOUND = 'ZONE_NOT_FOUND',
  ZONE_CAPACITY_EXCEEDED = 'ZONE_CAPACITY_EXCEEDED',
  PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND',
  INVALID_QUANTITY = 'INVALID_QUANTITY',
  ZONE_INACTIVE = 'ZONE_INACTIVE',
  STORAGE_RECORD_NOT_FOUND = 'STORAGE_RECORD_NOT_FOUND',
  UNAUTHORIZED_OPERATION = 'UNAUTHORIZED_OPERATION',
  DATABASE_CONNECTION_ERROR = 'DATABASE_CONNECTION_ERROR',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  DUPLICATE_ZONE_CODE = 'DUPLICATE_ZONE_CODE',
  ZONE_IN_USE = 'ZONE_IN_USE',
  
  // Stock reservation errors
  RESERVATION_NOT_FOUND = 'RESERVATION_NOT_FOUND',
  RESERVATION_EXPIRED = 'RESERVATION_EXPIRED',
  RESERVATION_CONFLICT = 'RESERVATION_CONFLICT',
  INVALID_RESERVATION_STATUS = 'INVALID_RESERVATION_STATUS',
  RESERVATION_CANNOT_BE_MODIFIED = 'RESERVATION_CANNOT_BE_MODIFIED',
  
  // Zone transfer errors
  TRANSFER_NOT_FOUND = 'TRANSFER_NOT_FOUND',
  TRANSFER_ALREADY_EXECUTED = 'TRANSFER_ALREADY_EXECUTED',
  TRANSFER_CONFLICT = 'TRANSFER_CONFLICT',
  INVALID_TRANSFER_STATUS = 'INVALID_TRANSFER_STATUS',
  SAME_ZONE_TRANSFER = 'SAME_ZONE_TRANSFER',
  TRANSFER_CANNOT_BE_MODIFIED = 'TRANSFER_CANNOT_BE_MODIFIED',
  TRANSFER_EXECUTION_FAILED = 'TRANSFER_EXECUTION_FAILED'
}

export class WarehouseError extends Error {
  public readonly code: WarehouseErrorCode;
  public readonly details?: Record<string, unknown>;
  public readonly userMessage: string;

  constructor(
    code: WarehouseErrorCode,
    message: string,
    userMessage: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'WarehouseError';
    this.code = code;
    this.userMessage = userMessage;
    this.details = details;
  }
}

export class InsufficientStockError extends WarehouseError {
  constructor(available: number, required: number, productName?: string) {
    const productText = productName ? ` per ${productName}` : '';
    super(
      WarehouseErrorCode.INSUFFICIENT_STOCK,
      `Insufficient stock: required ${required}, available ${available}`,
      `Stock insufficiente${productText}. Disponibile: ${available}, Richiesto: ${required}`,
      { available, required, productName }
    );
  }
}

export class ZoneNotFoundError extends WarehouseError {
  constructor(identifier: string, type: 'id' | 'code' = 'id') {
    super(
      WarehouseErrorCode.ZONE_NOT_FOUND,
      `Zone not found: ${identifier}`,
      type === 'code' 
        ? `Zona con codice "${identifier}" non trovata`
        : `Zona non trovata`,
      { identifier, type }
    );
  }
}

export class ZoneCapacityExceededError extends WarehouseError {
  constructor(zoneId: string, zoneName: string, capacity: number, requested: number) {
    super(
      WarehouseErrorCode.ZONE_CAPACITY_EXCEEDED,
      `Zone capacity exceeded: ${zoneName} (${capacity} max, ${requested} requested)`,
      `Capacità zona "${zoneName}" superata. Massimo: ${capacity}, Richiesto: ${requested}`,
      { zoneId, zoneName, capacity, requested }
    );
  }
}

export class ProductNotFoundError extends WarehouseError {
  constructor(productId: string) {
    super(
      WarehouseErrorCode.PRODUCT_NOT_FOUND,
      `Product not found: ${productId}`,
      `Prodotto non trovato`,
      { productId }
    );
  }
}

export class InvalidQuantityError extends WarehouseError {
  constructor(quantity: number, reason: string) {
    super(
      WarehouseErrorCode.INVALID_QUANTITY,
      `Invalid quantity: ${quantity} (${reason})`,
      `Quantità non valida: ${quantity}. ${reason}`,
      { quantity, reason }
    );
  }
}

export class ZoneInactiveError extends WarehouseError {
  constructor(zoneId: string, zoneName?: string) {
    const displayName = zoneName || zoneId;
    super(
      WarehouseErrorCode.ZONE_INACTIVE,
      `Zone is inactive: ${displayName}`,
      `La zona "${displayName}" non è attiva`,
      { zoneId, zoneName }
    );
  }
}

export class StorageRecordNotFoundError extends WarehouseError {
  constructor(storageId: string) {
    super(
      WarehouseErrorCode.STORAGE_RECORD_NOT_FOUND,
      `Storage record not found: ${storageId}`,
      `Record di stoccaggio non trovato`,
      { storageId }
    );
  }
}

export class UnauthorizedOperationError extends WarehouseError {
  constructor(operation: string, reason?: string) {
    super(
      WarehouseErrorCode.UNAUTHORIZED_OPERATION,
      `Unauthorized operation: ${operation}${reason ? ` - ${reason}` : ''}`,
      `Operazione non autorizzata: ${operation}`,
      { operation, reason }
    );
  }
}

export class DatabaseConnectionError extends WarehouseError {
  constructor(originalError?: unknown) {
    const errorMsg = (originalError as Error)?.message || 'Unknown error';
    super(
      WarehouseErrorCode.DATABASE_CONNECTION_ERROR,
      `Database connection error: ${errorMsg}`,
      `Errore di connessione al database. Riprova più tardi.`,
      { originalError }
    );
  }
}

export class TransactionFailedError extends WarehouseError {
  constructor(operation: string, originalError?: unknown) {
    super(
      WarehouseErrorCode.TRANSACTION_FAILED,
      `Transaction failed for operation: ${operation}`,
      `Operazione fallita: ${operation}. Riprova più tardi.`,
      { operation, originalError }
    );
  }
}

export class DuplicateZoneCodeError extends WarehouseError {
  constructor(code: string) {
    super(
      WarehouseErrorCode.DUPLICATE_ZONE_CODE,
      `Zone code already exists: ${code}`,
      `Il codice zona "${code}" è già in uso`,
      { code }
    );
  }
}

export class ZoneInUseError extends WarehouseError {
  constructor(zoneId: string, zoneName?: string, itemCount?: number) {
    const displayName = zoneName || zoneId;
    const itemText = itemCount ? ` (${itemCount} prodotti)` : '';
    super(
      WarehouseErrorCode.ZONE_IN_USE,
      `Zone is in use and cannot be deleted: ${displayName}`,
      `La zona "${displayName}" contiene prodotti${itemText} e non può essere eliminata`,
      { zoneId, zoneName, itemCount }
    );
  }
}

interface DatabaseErrorWithCode {
  code?: string;
  constraint?: string;
  details?: string;
  message?: string;
}

export function createWarehouseError(error: unknown, context?: string): WarehouseError {
  if (error instanceof WarehouseError) {
    return error;
  }

  // Handle Supabase/PostgreSQL errors
  const dbError = error as DatabaseErrorWithCode;
  if (dbError?.code) {
    switch (dbError.code) {
      case '23505': // unique_violation
        if (dbError.constraint?.includes('zone_code')) {
          return new DuplicateZoneCodeError(dbError.details || 'unknown');
        }
        break;
      case '23503': // foreign_key_violation
        return new ProductNotFoundError(dbError.details || 'unknown');
      case 'PGRST116': // Row not found
        if (context?.includes('zone')) {
          return new ZoneNotFoundError('unknown');
        }
        if (context?.includes('storage')) {
          return new StorageRecordNotFoundError('unknown');
        }
        break;
    }
  }

  // Handle authentication errors
  if (dbError?.message?.includes('not authenticated') || dbError?.message?.includes('auth')) {
    return new UnauthorizedOperationError(context || 'unknown', 'User not authenticated');
  }

  // Handle network/connection errors
  if (dbError?.message?.includes('network') || dbError?.message?.includes('connection')) {
    return new DatabaseConnectionError(error);
  }

  // Default to transaction failed
  return new TransactionFailedError(context || 'unknown operation', error);
}

export const ERROR_MESSAGES: Record<WarehouseErrorCode, { 
  title: string; 
  description: string; 
  action?: string 
}> = {
  [WarehouseErrorCode.INSUFFICIENT_STOCK]: {
    title: 'Stock Insufficiente',
    description: 'Non c\'è abbastanza stock disponibile per completare l\'operazione',
    action: 'Verifica le quantità disponibili e riprova'
  },
  [WarehouseErrorCode.ZONE_NOT_FOUND]: {
    title: 'Zona Non Trovata',
    description: 'La zona specificata non esiste o non è accessibile',
    action: 'Verifica il codice zona o seleziona una zona valida'
  },
  [WarehouseErrorCode.ZONE_CAPACITY_EXCEEDED]: {
    title: 'Capacità Zona Superata',
    description: 'La zona ha raggiunto la capacità massima',
    action: 'Scegli una zona diversa o libera spazio'
  },
  [WarehouseErrorCode.PRODUCT_NOT_FOUND]: {
    title: 'Prodotto Non Trovato',
    description: 'Il prodotto specificato non esiste',
    action: 'Verifica il codice prodotto'
  },
  [WarehouseErrorCode.INVALID_QUANTITY]: {
    title: 'Quantità Non Valida',
    description: 'La quantità inserita non è valida',
    action: 'Inserisci una quantità maggiore di zero'
  },
  [WarehouseErrorCode.ZONE_INACTIVE]: {
    title: 'Zona Inattiva',
    description: 'La zona selezionata non è attiva',
    action: 'Seleziona una zona attiva'
  },
  [WarehouseErrorCode.STORAGE_RECORD_NOT_FOUND]: {
    title: 'Record Non Trovato',
    description: 'Il record di stoccaggio non esiste',
    action: 'Aggiorna la lista e riprova'
  },
  [WarehouseErrorCode.UNAUTHORIZED_OPERATION]: {
    title: 'Operazione Non Autorizzata',
    description: 'Non hai i permessi per eseguire questa operazione',
    action: 'Contatta l\'amministratore'
  },
  [WarehouseErrorCode.DATABASE_CONNECTION_ERROR]: {
    title: 'Errore di Connessione',
    description: 'Problemi di connessione al database',
    action: 'Verifica la connessione e riprova'
  },
  [WarehouseErrorCode.TRANSACTION_FAILED]: {
    title: 'Operazione Fallita',
    description: 'L\'operazione non è stata completata correttamente',
    action: 'Riprova più tardi'
  },
  [WarehouseErrorCode.DUPLICATE_ZONE_CODE]: {
    title: 'Codice Zona Duplicato',
    description: 'Il codice zona è già in uso',
    action: 'Utilizza un codice diverso'
  },
  [WarehouseErrorCode.ZONE_IN_USE]: {
    title: 'Zona in Uso',
    description: 'La zona contiene prodotti e non può essere eliminata',
    action: 'Sposta tutti i prodotti prima di eliminare la zona'
  },
  
  // Stock reservation error messages
  [WarehouseErrorCode.RESERVATION_NOT_FOUND]: {
    title: 'Prenotazione Non Trovata',
    description: 'La prenotazione specificata non esiste o non è accessibile',
    action: 'Verifica l\'ID prenotazione'
  },
  [WarehouseErrorCode.RESERVATION_EXPIRED]: {
    title: 'Prenotazione Scaduta',
    description: 'La prenotazione è scaduta e non può più essere utilizzata',
    action: 'Crea una nuova prenotazione se necessario'
  },
  [WarehouseErrorCode.RESERVATION_CONFLICT]: {
    title: 'Conflitto Prenotazione',
    description: 'Non c\'è abbastanza stock disponibile per la prenotazione richiesta',
    action: 'Riduci la quantità o scegli un\'altra zona'
  },
  [WarehouseErrorCode.INVALID_RESERVATION_STATUS]: {
    title: 'Stato Prenotazione Non Valido',
    description: 'L\'operazione non può essere eseguita con lo stato attuale della prenotazione',
    action: 'Verifica lo stato della prenotazione'
  },
  [WarehouseErrorCode.RESERVATION_CANNOT_BE_MODIFIED]: {
    title: 'Prenotazione Non Modificabile',
    description: 'La prenotazione non può essere modificata nel suo stato attuale',
    action: 'Le prenotazioni scadute o completate non possono essere modificate'
  },
  
  // Zone transfer error messages
  [WarehouseErrorCode.TRANSFER_NOT_FOUND]: {
    title: 'Trasferimento Non Trovato',
    description: 'Il trasferimento specificato non esiste o non è accessibile',
    action: 'Verifica l\'ID trasferimento'
  },
  [WarehouseErrorCode.TRANSFER_ALREADY_EXECUTED]: {
    title: 'Trasferimento Già Eseguito',
    description: 'Il trasferimento è già stato completato e non può essere modificato',
    action: 'I trasferimenti completati non possono essere modificati'
  },
  [WarehouseErrorCode.TRANSFER_CONFLICT]: {
    title: 'Conflitto Trasferimento',
    description: 'Il trasferimento non può essere eseguito per mancanza di stock o capacità',
    action: 'Verifica disponibilità nelle zone coinvolte'
  },
  [WarehouseErrorCode.INVALID_TRANSFER_STATUS]: {
    title: 'Stato Trasferimento Non Valido',
    description: 'L\'operazione non può essere eseguita con lo stato attuale del trasferimento',
    action: 'Verifica lo stato del trasferimento'
  },
  [WarehouseErrorCode.SAME_ZONE_TRANSFER]: {
    title: 'Trasferimento Zona Identica',
    description: 'Non è possibile trasferire prodotti nella stessa zona',
    action: 'Seleziona una zona di destinazione diversa'
  },
  [WarehouseErrorCode.TRANSFER_CANNOT_BE_MODIFIED]: {
    title: 'Trasferimento Non Modificabile',
    description: 'Il trasferimento non può essere modificato nel suo stato attuale',
    action: 'Solo i trasferimenti in attesa possono essere modificati'
  },
  [WarehouseErrorCode.TRANSFER_EXECUTION_FAILED]: {
    title: 'Esecuzione Trasferimento Fallita',
    description: 'Si è verificato un errore durante l\'esecuzione del trasferimento',
    action: 'Verifica i log per i dettagli dell\'errore'
  }
};

// Stock reservation specific error classes
export class StockReservationError extends WarehouseError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(
      WarehouseErrorCode.RESERVATION_NOT_FOUND,
      message,
      'Errore nella gestione delle prenotazioni stock',
      details
    );
  }
}

export class ReservationExpiredError extends WarehouseError {
  constructor(message: string, expiryDate?: string) {
    super(
      WarehouseErrorCode.RESERVATION_EXPIRED,
      message,
      expiryDate
        ? `Prenotazione scaduta il ${new Date(expiryDate).toLocaleString('it-IT')}`
        : 'Prenotazione scaduta',
      { expiryDate }
    );
  }
}

export class ReservationConflictError extends WarehouseError {
  constructor(message: string, conflictInfo?: Record<string, unknown>) {
    super(
      WarehouseErrorCode.RESERVATION_CONFLICT,
      message,
      'Stock insufficiente per la prenotazione richiesta',
      conflictInfo
    );
  }
}

export class InvalidReservationStatusError extends WarehouseError {
  constructor(currentStatus: string, attemptedOperation: string) {
    super(
      WarehouseErrorCode.INVALID_RESERVATION_STATUS,
      `Cannot ${attemptedOperation} reservation with status: ${currentStatus}`,
      `Impossibile ${attemptedOperation} prenotazione con stato: ${currentStatus}`,
      { currentStatus, attemptedOperation }
    );
  }
}

export class ReservationCannotBeModifiedError extends WarehouseError {
  constructor(reservationId: string, reason: string) {
    super(
      WarehouseErrorCode.RESERVATION_CANNOT_BE_MODIFIED,
      `Reservation ${reservationId} cannot be modified: ${reason}`,
      `La prenotazione non può essere modificata: ${reason}`,
      { reservationId, reason }
    );
  }
}

// Zone transfer specific error classes
export class ZoneTransferError extends WarehouseError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(
      WarehouseErrorCode.TRANSFER_NOT_FOUND,
      message,
      'Errore nella gestione dei trasferimenti tra zone',
      details
    );
  }
}

export class TransferAlreadyExecutedError extends WarehouseError {
  constructor(transferId: string) {
    super(
      WarehouseErrorCode.TRANSFER_ALREADY_EXECUTED,
      `Transfer ${transferId} has already been executed`,
      'Il trasferimento è già stato eseguito e non può essere modificato',
      { transferId }
    );
  }
}

export class TransferConflictError extends WarehouseError {
  constructor(message: string, conflictInfo?: Record<string, unknown>) {
    super(
      WarehouseErrorCode.TRANSFER_CONFLICT,
      message,
      'Conflitto durante il trasferimento tra zone',
      conflictInfo
    );
  }
}

export class InvalidTransferStatusError extends WarehouseError {
  constructor(currentStatus: string, attemptedOperation: string) {
    super(
      WarehouseErrorCode.INVALID_TRANSFER_STATUS,
      `Cannot ${attemptedOperation} transfer with status: ${currentStatus}`,
      `Impossibile ${attemptedOperation} trasferimento con stato: ${currentStatus}`,
      { currentStatus, attemptedOperation }
    );
  }
}

export class SameZoneTransferError extends WarehouseError {
  constructor(zoneId: string, zoneName?: string) {
    const displayName = zoneName || zoneId;
    super(
      WarehouseErrorCode.SAME_ZONE_TRANSFER,
      `Cannot transfer within the same zone: ${displayName}`,
      `Impossibile trasferire nella stessa zona: ${displayName}`,
      { zoneId, zoneName }
    );
  }
}

export class TransferCannotBeModifiedError extends WarehouseError {
  constructor(transferId: string, currentStatus: string) {
    super(
      WarehouseErrorCode.TRANSFER_CANNOT_BE_MODIFIED,
      `Transfer ${transferId} cannot be modified with status: ${currentStatus}`,
      `Il trasferimento non può essere modificato con stato: ${currentStatus}`,
      { transferId, currentStatus }
    );
  }
}

export class TransferExecutionFailedError extends WarehouseError {
  constructor(transferId: string, reason: string, details?: Record<string, unknown>) {
    super(
      WarehouseErrorCode.TRANSFER_EXECUTION_FAILED,
      `Transfer execution failed: ${reason}`,
      `Esecuzione trasferimento fallita: ${reason}`,
      { transferId, reason, ...details }
    );
  }
}